import * as React from "react"
import { WidgetCardBaseProps, WidgetCardType as WidgetCardEnum, WidgetCard as WidgetCardType } from "../../types"
import styled, { css } from "styled-components"
import Icon from "../Icons/Icon"
import { Style } from "../../styles"
import Card from "../../widget/components/Home/Card"
import HomeWidgetCard from "../../widget/components/Home/WidgetCard"
import { useTranslation } from "react-i18next"
import { Draggable } from '@hello-pangea/dnd'

const Container = styled.div`
  background: #f8f8f7;
  position: relative;
  border-radius: 6px;
  padding: 16px;
  min-width: 348px;
  max-width: 448px;
  color: #1a1a1a;
  display: flex;
  flex-direction: row;

  @media screen and (max-width: 480px) {
    min-width: 100%;
    max-width: 100%;
  }
`

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  ${Card} {
    margin-right: 20px;
    padding: 8px;
  }
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
`
const Description = styled.div`
  margin-bottom: ${Style.spacing.x2};
`

const Actions = styled.div<{ mode: 'add' | 'edit' }>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${props => props.mode === 'add' && css`
    position: absolute;
    right: 10px;
  `}
`

const DragHandle = styled.div`
  width: 16px;
  height: 16px;
`

const Action = styled.div<{ additive?: boolean, desctructive?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  i { 
    font-size: 14px;
  }

  svg {
    width: 14px;
    height: 14px;

    ${props => props.desctructive && css`
      fill: ${Style.color.brandDanger};
      color: ${Style.color.brandDanger};
    `}
  }

  ${props => props.additive && css`
    width: 24px;
    height: 24px;
    background: #c7f1c6;
    border-radius: 6px;
  `}
`

type IProps = WidgetCardBaseProps & {
  card: WidgetCardType
  mode: 'add' | 'edit'
  index: number
  onAddCardClick?: (card: WidgetCardType) => void
  onSettingsClick?: (card: WidgetCardType) => void
  onRemoveCardClick?: (card: WidgetCardType) => void
  isDragDisabled: boolean
}

const WidgetCard = (props: IProps) => {
  const { t } = useTranslation()
  const { index, widget, card, mode, isDragDisabled } = props

  const getContent = (): { title: string, description: string } => {
    switch (card.type) {
      case WidgetCardEnum.NEW_CONVERSATION:
        return {
          title: t('WidgetCard::New conversation'),
          description: t('WidgetCard::Let visitors start a conversation')
        }
      case WidgetCardEnum.HELP_CENTER:
        return {
          title: t('WidgetCard::Article search'),
          description: t('WidgetCard::Help visitors self-serve before they start a conversation')
        }
      case WidgetCardEnum.NEWS:
        return {
          title: t('WidgetCard::News'),
          description: t('WidgetCard::Feature your most recent News item')
        }
      case WidgetCardEnum.POST:
        return {
          title: t('WidgetCard::Post'),
          description: t('WidgetCard::Feature a post to your visitors')
        }
      case WidgetCardEnum.EXTERNAL_LINK:
        return {
          title: t('WidgetCard::External links'),
          description: t('WidgetCard::Showcase additional links to your visitors')
        }
      default:
        throw new Error(`Unknown widget card type`)
    }
  }

  const { title, description } = getContent()

  const onAddCardClick = () => {
    if (props.onAddCardClick) props.onAddCardClick(card)
  }

  const onSettingsClick = () => {
    if (props.onSettingsClick) props.onSettingsClick(card)
  }

  const onRemoveCardClick = () => {
    if (props.onRemoveCardClick) props.onRemoveCardClick(card)
  }

  return (
    <Draggable key={card.id} draggableId={card.id} index={index} isDragDisabled={isDragDisabled}>
      {provided => (
        <Container
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <Content>
            <Title>{title}</Title>
            <Description>{description}</Description>

            <HomeWidgetCard
              widget={widget}
              locale={widget.locale}
              card={card}
              admin={true}
            />
          </Content>

          <Actions mode={mode}>
            {mode === 'add' && props.onAddCardClick && (
              <>
                <Action additive onClick={onAddCardClick}>
                  <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M14 7.15H8.85V2H7.15V7.15H2V8.85H7.15V14H8.85V8.85H14V7.15Z"></path></svg>
                </Action>
              </>
            )}

            {mode === 'edit' && (
              <>
                <DragHandle {...provided.dragHandleProps}>
                  <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M13 9C12.17 9 11.5 9.67 11.5 10.5C11.5 11.33 12.17 12 13 12C13.83 12 14.5 11.33 14.5 10.5C14.5 9.67 13.83 9 13 9ZM3 9C2.17 9 1.5 9.67 1.5 10.5C1.5 11.33 2.17 12 3 12C3.83 12 4.5 11.33 4.5 10.5C4.5 9.67 3.83 9 3 9ZM8 9C7.17 9 6.5 9.67 6.5 10.5C6.5 11.33 7.17 12 8 12C8.83 12 9.5 11.33 9.5 10.5C9.5 9.67 8.83 9 8 9ZM13 7C13.83 7 14.5 6.33 14.5 5.5C14.5 4.67 13.83 4 13 4C12.17 4 11.5 4.67 11.5 5.5C11.5 6.33 12.17 7 13 7ZM3 4C2.17 4 1.5 4.67 1.5 5.5C1.5 6.33 2.17 7 3 7C3.83 7 4.5 6.33 4.5 5.5C4.5 4.67 3.83 4 3 4ZM8 4C7.17 4 6.5 4.67 6.5 5.5C6.5 6.33 7.17 7 8 7C8.83 7 9.5 6.33 9.5 5.5C9.5 4.67 8.83 4 8 4Z"></path></svg>
                </DragHandle>
                {props.onSettingsClick && <Action onClick={onSettingsClick}>
                  <Icon icon='setting' />
                </Action>}
                {props.onRemoveCardClick && <Action desctructive onClick={onRemoveCardClick}>
                  <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M8 1C4.13 1 1 4.13 1 8C1 11.87 4.13 15 8 15C11.87 15 15 11.87 15 8C15 4.13 11.87 1 8 1ZM8 13.3C5.08 13.3 2.7 10.92 2.7 8C2.7 5.08 5.08 2.7 8 2.7C10.92 2.7 13.3 5.08 13.3 8C13.3 10.92 10.92 13.3 8 13.3ZM4.5 8.85H11.5V7.15H4.5V8.85Z"></path></svg>
                </Action>}
              </>
            )}
          </Actions>
        </Container>
      )}
    </Draggable>
  )
}

export default WidgetCard